import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DynamicSurveyFields, validatePublicSurveyField } from '../components/PublicSurveyForm';
import type { FormField, FormStructure } from '../features/surveys/types';
import type { QmsRegistryKind } from '../features/qms/qmsApi';
import { createQmsRecord, fetchActiveFormVersion, fetchConfigurableLists } from '../features/qms/qmsApi';

interface Props {
  kind: QmsRegistryKind;
  title: string;
  basePath: string;
}

export function QmsNewRecordPage({ kind, title, basePath }: Props) {
  const navigate = useNavigate();
  const [structure, setStructure] = useState<FormStructure | null>(null);
  const [formVersionId, setFormVersionId] = useState('');
  const [lists, setLists] = useState<{ key: string; items: { id: string; label: string }[] }[]>([]);

  const [incidentDate, setIncidentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [originType, setOriginType] = useState<'internal' | 'external'>('internal');
  const [typificationMode, setTypificationMode] = useState<'by_area' | 'by_process'>('by_area');
  const [typificationListItemId, setTypificationListItemId] = useState('');
  const [attributability, setAttributability] = useState<
    'company' | 'supplier' | 'client' | 'external_carrier'
  >('company');
  const [description, setDescription] = useState('');
  const [dynamicData, setDynamicData] = useState<Record<string, unknown>>({});
  const [dynErrors, setDynErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [fv, allLists] = await Promise.all([fetchActiveFormVersion(kind), fetchConfigurableLists()]);
        setStructure((fv.structure as FormStructure) ?? { fields: [] });
        setFormVersionId(fv.id);
        setLists(allLists.map((l) => ({ key: l.key, items: l.items })));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error cargando formulario');
      }
    })();
  }, [kind]);

  const listKey = typificationMode === 'by_area' ? 'typification_area' : 'typification_process';
  const listItems =
    kind === 'claim' ? lists.find((l) => l.key === listKey)?.items ?? [] : [];

  const validateDynField = (fieldId: string, value: unknown) => {
    const field = structure?.fields.find((f) => f.id === fieldId);
    if (!field) return;
    const message = validatePublicSurveyField(field, value);
    setDynErrors((prev) => {
      const next = { ...prev };
      if (message) next[fieldId] = message;
      else delete next[fieldId];
      return next;
    });
  };

  const handleDynChange = (fieldId: string, value: unknown) => {
    setDynamicData((prev) => ({ ...prev, [fieldId]: value }));
    const field = structure?.fields.find((f) => f.id === fieldId);
    if (dynErrors[fieldId] || field?.type === 'number' || field?.type === 'date') {
      validateDynField(fieldId, value);
    }
  };

  const handleDynBlur = (fieldId: string) => {
    validateDynField(fieldId, dynamicData[fieldId]);
  };

  const handleCheckboxMultipleChange = (fieldId: string, option: string, checked: boolean) => {
    const current = (dynamicData[fieldId] as string[]) || [];
    const next = checked ? [...current, option] : current.filter((x) => x !== option);
    handleDynChange(fieldId, next);
  };

  const handleSubmitAll = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const errs: Record<string, string> = {};
    if (!description.trim()) {
      setError('La descripción es obligatoria');
      return;
    }
    const str = structure ?? { fields: [] };
    str.fields.forEach((field: FormField) => {
      const err = validatePublicSurveyField(field, dynamicData[field.id]);
      if (err) errs[field.id] = err;
    });
    if (Object.keys(errs).length > 0) {
      setDynErrors(errs);
      return;
    }
    setDynErrors({});
    setLoading(true);
    try {
      const res = await createQmsRecord({
        registryKind: kind,
        incidentDate: new Date(incidentDate).toISOString(),
        originType,
        typificationMode,
        typificationListItemId: kind === 'claim' ? typificationListItemId || undefined : undefined,
        attributability,
        description,
        formVersionId,
        dynamicData,
      });
      navigate(`${basePath}/${res.id}`, { replace: true, state: { created: true } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  if (!structure) {
    return (
      <div className="container max-w-3xl py-8 px-4">
        <p className="text-muted-foreground">{error || 'Cargando…'}</p>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-8 px-4">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">{title} — Alta</h1>
        <Link to={basePath} className="text-sm text-primary hover:underline">
          Volver al listado
        </Link>
      </div>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <form onSubmit={handleSubmitAll} className="space-y-6 rounded-lg border border-border bg-card p-6">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Datos del incidente</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium mb-1">Fecha del incidente</label>
            <input
              type="date"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo (origen)</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={originType}
              onChange={(e) => setOriginType(e.target.value as 'internal' | 'external')}
            >
              <option value="internal">Interno</option>
              <option value="external">Externo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipificación</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={typificationMode}
              onChange={(e) => setTypificationMode(e.target.value as 'by_area' | 'by_process')}
            >
              <option value="by_area">Por área</option>
              <option value="by_process">Por proceso</option>
            </select>
          </div>
          {kind === 'claim' && (
            <div>
              <label className="block text-sm font-medium mb-1">Ítem de tipificación</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={typificationListItemId}
                onChange={(e) => setTypificationListItemId(e.target.value)}
              >
                <option value="">—</option>
                {listItems.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1">Atribuibilidad</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={attributability}
              onChange={(e) => setAttributability(e.target.value as typeof attributability)}
            >
              <option value="company">Empresa</option>
              <option value="supplier">Proveedor</option>
              <option value="client">Cliente</option>
              <option value="external_carrier">Transportista externo</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Descripción</label>
          <textarea
            className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>

        {structure.fields.length > 0 && (
          <>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2 border-t border-border">
              Campos adicionales
            </h2>
            <DynamicSurveyFields
              structure={structure}
              formData={dynamicData}
              errors={dynErrors}
              onChange={handleDynChange}
              onCheckboxMultipleChange={handleCheckboxMultipleChange}
              onFieldBlur={handleDynBlur}
            />
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Enviando…' : 'Registrar'}
        </button>
      </form>
    </div>
  );
}
